import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { templatesAPI, contractTypesAPI } from '../services/api'
import type { ContractTemplate, ContractType } from '../types'

export default function TemplateEditor() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const isEditing = !!id

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [htmlContent, setHtmlContent] = useState('')
  const [cssStyles, setCssStyles] = useState('')
  const [contractTypeId, setContractTypeId] = useState<number | null>(null)
  const [locale, setLocale] = useState('es')
  const [contractTypes, setContractTypes] = useState<ContractType[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(false)

  useEffect(() => {
    const load = async () => {
      try {
        const [typesResp] = await Promise.all([
          contractTypesAPI.list(),
        ])
        setContractTypes(typesResp.data.contract_types || [])

        if (isEditing && id) {
          const resp = await templatesAPI.getById(Number(id))
          const tpl = resp.data
          setName(tpl.name)
          setDescription(tpl.description || '')
          setHtmlContent(tpl.html_content)
          setCssStyles(tpl.css_styles || '')
          setContractTypeId(tpl.contract_type_id)
          setLocale(tpl.locale)
        }
      } catch (err) {
        console.error('Failed to load', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id])

  const handleSave = async () => {
    setSaving(true)
    try {
      const data = {
        name,
        description,
        html_content: htmlContent,
        css_styles: cssStyles,
        contract_type_id: contractTypeId,
        locale,
      }

      if (isEditing && id) {
        await templatesAPI.update(Number(id), data)
      } else {
        await templatesAPI.create(data)
      }
      navigate('/templates')
    } catch (err) {
      console.error('Failed to save', err)
      alert('Error al guardar la plantilla')
    } finally {
      setSaving(false)
    }
  }

  const insertVariable = (key: string) => {
    setHtmlContent((prev) => prev + `{{${key}}}`)
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    )
  }

  const builtInVariables = [
    { key: 'provider.name', label: 'Nombre proveedor' },
    { key: 'provider.nif', label: 'NIF proveedor' },
    { key: 'provider.address', label: 'Dirección proveedor' },
    { key: 'client.name', label: 'Nombre cliente' },
    { key: 'client.nif', label: 'NIF cliente' },
    { key: 'client.address', label: 'Dirección cliente' },
    { key: 'contract.number', label: 'Nº contrato' },
    { key: 'contract.date', label: 'Fecha' },
    { key: 'contract.price', label: 'Precio' },
    { key: 'services', label: 'Servicios' },
    { key: 'contacts', label: 'Contactos' },
  ]

  return (
    <div>
      <div className="page-header">
        <h1><i className="bi bi-files me-2"></i>
          {isEditing ? `Editar: ${name}` : t('template.new')}
        </h1>
        <div>
          <button className="btn btn-secondary me-2" onClick={() => navigate('/templates')}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-outline-info me-2" onClick={() => setShowPreview(!showPreview)}>
            <i className="bi bi-eye me-1"></i> {t('template.preview')}
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-check-lg me-1"></i>}
            {t('common.save')}
          </button>
        </div>
      </div>

      <div className="row g-3">
        {/* Editor */}
        <div className={`col-md-${showPreview ? '6' : '9'}`}>
          <div className="card mb-3">
            <div className="card-header">Información</div>
            <div className="card-body">
              <div className="row g-2">
                <div className="col-md-6">
                  <label className="form-label">Nombre</label>
                  <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={contractTypeId || ''} onChange={(e) => setContractTypeId(Number(e.target.value) || null)}>
                    <option value="">Sin tipo</option>
                    {contractTypes.map((ct) => (
                      <option key={ct.id} value={ct.id}>{ct.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Idioma</label>
                  <select className="form-select" value={locale} onChange={(e) => setLocale(e.target.value)}>
                    <option value="es">Español</option>
                    <option value="en">English</option>
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Descripción</label>
                  <textarea className="form-control" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span>Contenido HTML</span>
              <div className="dropdown">
                <button className="btn btn-sm btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown">
                  <i className="bi bi-plus-circle me-1"></i> Insertar variable
                </button>
                <ul className="dropdown-menu" style={{ maxHeight: '300px', overflow: 'auto' }}>
                  {builtInVariables.map((v) => (
                    <li key={v.key}>
                      <button className="dropdown-item small" onClick={() => insertVariable(v.key)}>
                        <code>{`{{${v.key}}}`}</code>
                        <br /><small className="text-muted">{v.label}</small>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="card-body p-0">
              <textarea
                className="form-control border-0 rounded-0"
                style={{ minHeight: '400px', fontFamily: 'monospace', fontSize: '13px' }}
                value={htmlContent}
                onChange={(e) => setHtmlContent(e.target.value)}
                placeholder="<!-- HTML content -->"
              />
            </div>
          </div>
        </div>

        {/* Preview */}
        {showPreview && (
          <div className="col-md-3">
            <div className="card">
              <div className="card-header">CSS personalizado</div>
              <div className="card-body p-0">
                <textarea
                  className="form-control border-0 rounded-0"
                  style={{ minHeight: '150px', fontFamily: 'monospace', fontSize: '12px' }}
                  value={cssStyles}
                  onChange={(e) => setCssStyles(e.target.value)}
                  placeholder="/* CSS */"
                />
              </div>
            </div>
            <div className="card mt-3" style={{ position: 'sticky', top: '80px' }}>
              <div className="card-header">{t('template.preview')}</div>
              <div className="card-body p-2" style={{ maxHeight: '60vh', overflow: 'auto' }}>
                <div className="ratio ratio-4x3">
                  <iframe
                    srcDoc={htmlContent}
                    title="Preview"
                    style={{ border: 'none', background: 'white', borderRadius: '4px' }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
